import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { hubError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { ListChecks, Upload, Download, Loader2, Trash2, CheckCircle2, Clock, FileSpreadsheet, Search, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from './Pagination';
import { usePagination } from '../hooks/usePagination';

const TEMPLATE = 'nombre,email,telefono,pastor,ciudad,pais,notas\nIglesia Ejemplo,contacto@ejemplo.org,+58 212 555 0100,Pastor Ejemplo,Caracas,Venezuela,Fundadora\n';

export const GenesisRosterCard = ({ onImported }) => {
  const [roster, setRoster] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [linking, setLinking] = useState(null);
  const [churches, setChurches] = useState([]);
  const [churchSearch, setChurchSearch] = useState('');
  const [churchesLoading, setChurchesLoading] = useState(false);
  const [unlinking, setUnlinking] = useState(null);
  const fileRef = useRef(null);
  const pg = usePagination(search.trim().toLowerCase());

  const fetchRoster = useCallback(() => {
    setLoading(true);
    api.get('/api/subscriptions/genesis-roster', { params: { search: search.trim() || undefined, offset: pg.offset, limit: pg.pageSize } })
      .then(res => {
        setRoster(res.data?.data || []);
        setCounts(res.data);
      })
      .catch(err => toast.error(hubError(err, 'Error al cargar el padrón')))
      .finally(() => setLoading(false));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pg.offset, pg.pageSize]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'padron_genesis.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const pickFile = async (selected, replaceFlag = replace) => {
    setFile(selected);
    setPreview(null);
    if (!selected) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', selected);
      const res = await api.post('/api/subscriptions/genesis-roster/import', form, {
        params: { dry_run: true, replace: replaceFlag },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(hubError(err, 'No se pudo leer el archivo'));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/api/subscriptions/genesis-roster/import', form, {
        params: { dry_run: false, replace },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Padrón actualizado: ${res.data?.created || 0} nuevas y ${res.data?.updated || 0} actualizadas`);
      if (res.data?.claimed_now?.length) {
        toast.info(`${res.data.claimed_now.length} ya estaban registradas y pasaron a Plan Génesis`);
      }
      setDialogOpen(false);
      setFile(null);
      setPreview(null);
      fetchRoster();
      onImported?.();
    } catch (err) {
      toast.error(hubError(err, 'Error al importar el padrón'));
    } finally {
      setBusy(false);
    }
  };

  const openLink = (row) => {
    setLinking(row);
    setChurchSearch('');
    setChurchesLoading(true);
    api.get('/api/subscriptions/genesis-roster/churches')
      .then(res => setChurches(res.data?.data || []))
      .catch(err => toast.error(hubError(err, 'No se pudieron cargar las congregaciones')))
      .finally(() => setChurchesLoading(false));
  };

  const confirmLink = async (church) => {
    setBusy(true);
    try {
      await api.post(`/api/subscriptions/genesis-roster/${linking.id}/link`, { church_id: church.id });
      toast.success(`${church.name} quedó vinculada a «${linking.name}» con Plan Génesis`);
      setLinking(null);
      fetchRoster();
      onImported?.();
    } catch (err) {
      toast.error(hubError(err, 'No se pudo vincular'));
    } finally { setBusy(false); }
  };

  const confirmUnlink = async () => {
    try {
      await api.delete(`/api/subscriptions/genesis-roster/${unlinking.id}/link`);
      toast.success('Vínculo deshecho. Ajusta el plan desde la tabla de suscripciones si hace falta');
      setUnlinking(null);
      fetchRoster();
      onImported?.();
    } catch (err) { toast.error(hubError(err, 'No se pudo desvincular')); }
  };

  const filteredChurches = churches.filter(c => {
    const q = churchSearch.trim().toLowerCase();
    if (!q) return true;
    return `${c.name} ${c.pastor} ${c.email}`.toLowerCase().includes(q);
  });

  const removeEntry = async () => {
    try {
      await api.delete(`/api/subscriptions/genesis-roster/${removing.id}`);
      toast.success('Congregación quitada del padrón');
      setRemoving(null);
      fetchRoster();
      onImported?.();
    } catch (err) { toast.error(hubError(err, 'No se pudo quitar')); }
  };

  return (
    <Card data-testid="genesis-roster-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <ListChecks className="w-5 h-5" /> Padrón Génesis
          <Badge variant="secondary" className="text-[11px]" data-testid="roster-seats-badge">
            {(counts?.genesis_seats ?? 0) - (counts?.genesis_pending ?? 0)} de {counts?.genesis_seats ?? 0} registradas
          </Badge>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="roster-template-button">
              <Download className="w-4 h-4 mr-1" /> Plantilla CSV
            </Button>
            <Button size="sm" className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={() => setDialogOpen(true)} data-testid="roster-upload-button">
              <Upload className="w-4 h-4 mr-1" /> Carga masiva
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Estas son las congregaciones fundadoras. Los cupos Génesis son exactamente las filas de
          este padrón: cuando una de ellas se registre en la app, la Suite la reconoce por el nombre
          de la congregación y su pastor (tolera variantes y tildes, no depende del correo) y le
          asigna el Plan Génesis automáticamente. Cualquier congregación que se registre fuera del
          padrón entra al Plan Ministerial de pago.
        </p>

        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchRoster()}
            placeholder="Buscar en el padrón..."
            className="pl-9"
            data-testid="roster-search-input"
          />
        </div>

        <Pagination
          total={counts?.total ?? 0} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="congregaciones del padrón" testId="roster-pagination"
        />

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 spinner text-[#056A84]" /></div>
        ) : roster.length === 0 ? (
          <div className="empty-state py-10">
            <FileSpreadsheet className="w-10 h-10 text-muted-foreground/40" />
            <p>El padrón está vacío. Sube el CSV con las congregaciones fundadoras.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="roster-list">
            {roster.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border" data-testid={`roster-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[r.email, r.pastor, r.city].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.church_id ? (
                    <>
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px]">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Registrada
                      </Badge>
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setUnlinking(r)} data-testid={`roster-unlink-${r.id}`}>
                        <Unlink className="w-3.5 h-3.5 mr-1" /> Desvincular
                      </Button>
                    </>
                  ) : (
                    <>
                      <Badge variant="secondary" className="text-[10px]">
                        <Clock className="w-3 h-3 mr-1" /> Por registrarse
                      </Badge>
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => openLink(r)} data-testid={`roster-link-${r.id}`}>
                        <Link2 className="w-3.5 h-3.5 mr-1" /> Vincular
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" className="text-destructive h-8" onClick={() => setRemoving(r)} data-testid={`roster-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setFile(null); setPreview(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Carga masiva del padrón Génesis</DialogTitle>
            <DialogDescription>
              Columnas aceptadas: <span className="font-mono text-[11px]">nombre, email, telefono, pastor, ciudad, pais, notas</span>.
              Solo <span className="font-medium">nombre</span> es obligatorio.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <Input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={e => pickFile(e.target.files?.[0] || null)}
              data-testid="roster-file-input"
            />

            <label className="flex items-center gap-3 p-2 rounded-lg border cursor-pointer hover:bg-accent/40">
              <Checkbox
                checked={replace}
                onCheckedChange={v => { setReplace(!!v); if (file) pickFile(file, !!v); }}
                data-testid="roster-replace-checkbox"
              />
              <span className="text-sm">Reemplazar el padrón actual (conserva las ya registradas)</span>
            </label>

            {busy && !preview && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 spinner" /> Leyendo el archivo...
              </div>
            )}

            {preview && (
              <div className="rounded-lg border p-3 space-y-2" data-testid="roster-preview">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-lg font-semibold text-[#056A84]" data-testid="preview-new">{preview.new}</p><p className="text-[11px] text-muted-foreground">nuevas</p></div>
                  <div><p className="text-lg font-semibold text-[#C58B2A]" data-testid="preview-duplicates">{preview.duplicates}</p><p className="text-[11px] text-muted-foreground">ya en el padrón</p></div>
                  <div><p className="text-lg font-semibold text-destructive" data-testid="preview-invalid">{preview.invalid_total}</p><p className="text-[11px] text-muted-foreground">con error</p></div>
                </div>
                {preview.invalid?.length > 0 && (
                  <div className="text-[11px] text-destructive space-y-0.5">
                    {preview.invalid.map(i => <p key={i.row}>Fila {i.row}: {i.reason}</p>)}
                  </div>
                )}
                {preview.preview?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border-t pt-2 space-y-1">
                    {preview.preview.map((p, idx) => (
                      <p key={idx} className="text-xs truncate">
                        <span className="font-medium">{p.name}</span>
                        {p.email ? <span className="text-muted-foreground"> · {p.email}</span> : null}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              className="bg-[#056A84] hover:bg-[#045A70] text-white"
              onClick={confirmImport}
              disabled={busy || !preview || preview.valid === 0}
              data-testid="roster-import-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Importar {preview ? (preview.new === preview.valid ? `${preview.new} nuevas` : `${preview.new} nuevas (+${preview.duplicates} actualizadas)`) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linking} onOpenChange={(open) => { if (!open) setLinking(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Vincular «{linking?.name}»</DialogTitle>
            <DialogDescription>
              Elige la congregación ya registrada en la app que corresponde a esta fila del padrón.
              Al vincularla pasa de inmediato al Plan Génesis y se anulan sus facturas pendientes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={churchSearch}
                onChange={e => setChurchSearch(e.target.value)}
                placeholder="Buscar congregación o pastor..."
                className="pl-9"
                data-testid="link-church-search"
              />
            </div>
            {churchesLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 spinner text-[#056A84]" /></div>
            ) : filteredChurches.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No hay congregaciones que coincidan.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="link-church-list">
                {filteredChurches.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    disabled={busy || !!c.linked_to}
                    onClick={() => confirmLink(c)}
                    className="w-full text-left p-2.5 rounded-lg border hover:bg-accent/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid={`link-church-${c.id}`}
                  >
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {[c.pastor, c.email].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                      {c.linked_to ? ` · ya vinculada a «${c.linked_to}»` : ''}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinking(null)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!unlinking} onOpenChange={() => setUnlinking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Deshacer el vínculo?</AlertDialogTitle>
            <AlertDialogDescription>
              «{unlinking?.name}» volverá a quedar como por registrarse. El plan de la congregación
              no cambia automáticamente: ajústalo en la tabla de suscripciones si corresponde.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnlink} data-testid="roster-unlink-confirm">
              Desvincular
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removing} onOpenChange={() => setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar del padrón?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing?.name} dejará de tener cupo Génesis. Si todavía no se ha registrado, al
              hacerlo entrará al Plan Ministerial de pago.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={removeEntry} data-testid="roster-delete-confirm">
              Quitar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
